'use strict';

import ready from '../../../js/lib/ready';
import vsMandalaSource from '../glsl/vs-mandala.glsl';
import fsMandalaSource from '../glsl/fs-mandala.glsl';
import vsClickedSource from '../glsl/vs-mandala-clicked.glsl';
import fsClickedSource from '../glsl/fs-mandala-clicked.glsl';

import mandala1 from '../svg/mandala-1.svg';
import mandala2 from '../svg/mandala-2.svg';
import mandala3 from '../svg/mandala-3.svg';
import mandala4 from '../svg/mandala-4.svg';
import mandala5 from '../svg/mandala-5.svg';
import mandala6 from '../svg/mandala-6.svg';
import mandala7 from '../svg/mandala-7.svg';
import CanvasResize from '../../../js/lib/CanvasResize';
import debounceRaf from '../../../js/lib/debounce-raf';
import SmoothenChange from '../../../js/lib/smoothen-change';
import bezier from 'bezier-easing';
import promiseIdle from '../../../js/lib/promise-idle';
import * as twgl from 'twgl.js';

const m4 = twgl.m4;

function Mandala(node) {

    const components = [
        {
            s: mandala1, a: 12, o: 6, d: 266, z: 120,
        },
        {
            s: mandala2, a: 12, o: 0, d: 257, z: 100,
        },
        {
            s: mandala3, a: 4.5, o: 2.25, d: 316, z: 80,
        },
        {
            s: mandala4, a: 18, o: 10.3, d: 323, z: 60,
        },
        {
            s: mandala5, a: 18, o: 9, d: 367, z: 40,
        },
        {
            s: mandala6, a: 360, o: 0, d: -415, z: 30,
        },
        {
            s: mandala7, a: 10, o: -2.4, d: 419, z: 20,
        },
        {
            s: mandala3, a: (360 / 118), o: -3, d: 466, z: 0,
        },
        {
            s: mandala2, a: (360 / 106), o: 0, d: 482, w: 30, h: 31, z: -20,
        },
    ];
	
    const color = [32.0, 27.0, 24.0, 255.0];
    const toRad = 1 / 360 * Math.PI * 2;
    const field = 60 * toRad;
    const zoom = (60 * toRad) * 940 * (1200 / 1080);
    const cameraHeight = zoom / field;
    const cameraY = ((1080 - 1200) / 2);
    const zNear = 1;
    const zFar = 10000;
    const aMax = 15 * toRad;

    let startTime = null;
    // let time = 0;
    let enterDuration = 0;
    const enterZ = 1200;
    const enterEase = bezier(0, 0, .3, 1);

    // mouse interaction
    let ax = 0;
    let ay = 0;
    let mouseUpTimeoutId;
    let clicked = false;
    let glitchOffset = 0;

    this.initialized = false;

    const parser = new DOMParser();

    const canvas = document.createElement('canvas');
    node.appendChild(canvas);

    const gl = canvas.getContext('webgl');

    if (!gl) {
        return fallback(node);
    }

    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.enable(gl.BLEND);

    const mandalaProgramInfo = twgl.createProgramInfo(gl, [vsMandalaSource, fsMandalaSource]);
    const clickedProgramInfo = twgl.createProgramInfo(gl, [vsClickedSource, fsClickedSource]);

    let framebufferInfo;

    const clickedBufferInfo = twgl.createBufferInfoFromArrays(gl, {
        a_position: [
            -1.0, -1.0, 0,
            1.0, -1.0, 0,
            -1.0, 1.0, 0,
            -1.0, 1.0, 0,
            1.0, -1.0, 0,
            1.0, 1.0, 0,
        ],
        a_texCoord: [
            0, 0,
            1, 0,
            0, 1,
            0, 1,
            1, 0,
            1, 1,
        ],
    });

    const render = debounceRaf(drawScene);

    const animate = new SmoothenChange(function (nax, nay) {
        ay = nax;
        ax = nay;
        render();
    });

    const mouseMoveHandler = debounceRaf(function (e) {
        animate.run(
            Math.max(Math.min((e.clientX * 2 - canvas.clientWidth) / canvas.clientWidth * -aMax, aMax), -aMax),
            Math.max(Math.min((e.clientY * 2 - canvas.clientHeight) / canvas.clientHeight * -aMax, aMax), -aMax),
            {
                duration: 200,
            }
        );
    });

    const promises = components.map(loadComponent);
    promises.push(new CanvasResize(canvas, {dpr: 1}));

    Promise.all(promises).then(promiseIdle()).then(initScene);

    function fallback() {
        node.insertAdjacentHTML('afterbegin', node.querySelector('noscript').innerText);
    }

    this.unlisten = unlisten;
    function unlisten() {
        document.removeEventListener('mousemove', mouseMoveHandler);
        document.removeEventListener('mousedown', mouseDownHandler);
        document.removeEventListener('mouseup', mouseUpHandler);
    }

    function initScene() {

        const attachments = [
            {format: gl.RGBA, minMag: gl.LINEAR},
            {format: gl.DEPTH_STENCIL},
        ];

        framebufferInfo = twgl.createFramebufferInfo(gl, attachments, gl.drawingBufferWidth, gl.drawingBufferHeight);

        document.addEventListener('mousemove', mouseMoveHandler);

        getStartPromise().then(function () {
            document.addEventListener('mousedown', mouseDownHandler);
            document.addEventListener('mouseup', mouseUpHandler);
            enterAnimate();
        });

        render();
    }

    function getStartPromise() {
        return new Promise(function (resolve) {
            // setTimeout(function () {
                requestIdleCallback(resolve);
            // }, 500);
        });
    }

    function enterAnimate() {
        requestAnimationFrame(function (t) {
            if (startTime === null) {
                startTime = t;
            }
            render();
            t - startTime < enterDuration && enterAnimate();
        });
    }

    function mouseUpHandler() {
        if (mouseUpTimeoutId || !clicked) {
            return;
        }
        mouseUpTimeoutId = setTimeout(function () {
            mouseUpTimeoutId = null;
            clicked = false;
            render();
        }, 100);
    }

    function mouseDownHandler() {
        clicked = true;
        clearTimeout(mouseUpTimeoutId);
        glitch();
    }

    function glitch() {
        glitchOffset = [Math.random() * 2 - 1, Math.random() * 2 - 1];
        render();
        clicked && setTimeout(glitch, 500 * Math.random());
    }

    function loadComponent(c, index) {
        return new Promise(function (resolve) {
            requestIdleCallback(function () {
                c.count = Math.round(360 / c.a);
                c.o *= toRad;
                c.a *= toRad;
                // c.count = Math.round(Math.PI * 2 / c.a);
                c.scale = 1 - (c.z / cameraHeight);

                if (c.w === undefined || c.h === undefined) {
                    const svg = parser.parseFromString(c.s, 'image/svg+xml').documentElement;
                    c.w = parseFloat(svg.getAttribute('width'));
                    c.h = parseFloat(svg.getAttribute('height'));
                }

                c.matrices = [];
                for (let i = 0; i < c.count; ++i) {
                    const matrix = m4.identity();
                    m4.scale(matrix, [c.scale, c.scale, c.scale], matrix);
                    m4.rotateZ(matrix, c.o + c.a * i, matrix);
                    m4.translate(matrix, [-c.w * .5, c.d, c.z], matrix);
                    c.matrices.push(matrix);
                }

                c.bufferInfo = twgl.createBufferInfoFromArrays(gl, {
                    a_position: {
                        numComponents: 3,
                        data: [
                            0, 0, 0,
                            c.w, 0, 0,
                            0, c.h, 0,
                            0, c.h, 0,
                            c.w, 0, 0,
                            c.w, c.h, 0,
                        ],
                    },
                    a_texCoord: {
                        numComponents: 2,
                        data: [
                            0, 1,
                            1, 1,
                            0, 0,
                            0, 0,
                            1, 1,
                            1, 0,
                        ],
                    },
                });

                // Enter animation
                const ed = 1000;
                const ecd = Math.log(index + 1) * 400;
                const eed = 0;
                c.ea = enterAnimation(ed, ecd, eed);

                enterDuration = Math.max(enterDuration, ecd + c.count * eed + ed);

                // Texture
                c.texture = twgl.createTexture(gl, {
                    width: c.w,
                    height: c.h,
                    wrap: gl.CLAMP_TO_EDGE,
                    minMag: gl.LINEAR,
                    src: `data:image/svg+xml;utf8,${encodeURIComponent(c.s)}`,
                }, resolve);
            });
        });
    }

    function drawScene() {

        const time = startTime ? this - startTime : 0;

        twgl.resizeCanvasToDisplaySize(gl.canvas);

        if (clicked) {
            // render to framebuffer
            twgl.bindFramebufferInfo(gl, framebufferInfo);
        } else {
            // render to canvas
            twgl.bindFramebufferInfo(gl, null);
        }

        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        gl.useProgram(mandalaProgramInfo.program);

        // Projection
        var aspect = gl.drawingBufferWidth / gl.drawingBufferHeight;
        var projectionMatrix = m4.perspective(field, aspect, zNear, zFar);

        // Camera
        var cameraMatrix = m4.identity();
        m4.rotateX(cameraMatrix, ax, cameraMatrix);
        m4.rotateY(cameraMatrix, ay, cameraMatrix);
        m4.translate(cameraMatrix, [0, cameraY, cameraHeight], cameraMatrix);

        // View projection
        var viewProjectionMatrix = m4.multiply(projectionMatrix, m4.inverse(cameraMatrix));

        twgl.setUniforms(mandalaProgramInfo, {u_view: viewProjectionMatrix});

        components.forEach(function (c) {
            twgl.setBuffersAndAttributes(gl, mandalaProgramInfo, c.bufferInfo);
            twgl.setUniforms(mandalaProgramInfo, {
                u_texture: c.texture,
                u_transition: m4.translation([0, 0, c.ea(time, 0, enterZ, 0)]),
                u_color: [color[0], color[1], color[2], c.ea(time, 0, 0, color[3])],
            });

            for (let i = 0; i < c.count; ++i) {
                twgl.setUniforms(mandalaProgramInfo, {u_matrix: c.matrices[i]});
                twgl.drawBufferInfo(gl, c.bufferInfo);
            }
        });

        if (clicked) {
            // render to canvas
            twgl.bindFramebufferInfo(gl, null);
            gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
            gl.clearColor(0, 0, 0, 0);
            gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
            gl.useProgram(clickedProgramInfo.program);
            twgl.setBuffersAndAttributes(gl, clickedProgramInfo, clickedBufferInfo);
            twgl.setUniforms(clickedProgramInfo, {
                u_texture: framebufferInfo.attachments[0],
                u_random: glitchOffset,
                u_time: time,
                u_offset: 1 / gl.drawingBufferWidth,
            });
            twgl.drawBufferInfo(gl, clickedBufferInfo);
        }
    }

    function enterAnimation(duration, componentDelay, elementDelay) {
        return function (t, i, start, end) {
            const progress = Math.max(0, Math.min(duration, t - componentDelay - i * elementDelay)) / duration;
            return start + enterEase(progress) * (end - start);
        };
    }
}

Mandala.prototype.destroy = function () {
};

ready().then(function init() {

    let mandala = null;

    reload();
    document.addEventListener('reload', reload);

    function reload() {
        requestIdleCallback(function () {
            mandala && mandala.destroy();
            const node = document.querySelector('.mandala');
            if (!node) {
                return;
            }
            mandala = new Mandala(node);
        });
    }
});
